/**
 * Lead Drop Popup 9 — "Apply Now" single-step flow (Figma: Typography System · 4 Breakpoints).
 *
 * Flow:  Step 1 (full form + OPTIONAL "Admission Closed in" countdown)  →
 *        Step 2 (4-digit OTP)  →
 *        Step 3 (last card: Thank You / Welcome Back / Downloaded Successfully).
 *
 * The last cards share a hardcoded circular SVG top background whose color is
 * configurable via `success_circle_color`. The timer is OPTIONAL (toggle
 * `enable_timer`) and its field set mirrors Lead Drop Popup/Form 2.
 *
 * Field set is a focused superset of Popup 1 + 2: header, optional timer,
 * step-1 button + consent, OTP step, three last-card states, popup display
 * controls. New (non-DB-column) fields are flagged `isJsonField` so the server
 * nests them under `additional_fields` and flattens them back for the widget.
 */
export const LEAD_DROP_POPUP_9_CONSTANTS = {
  DEFAULT_ROW: {
    // Header
    form_title: "Apply Now",
    form_title_color: "#101112",
    form_sub_text: "For free career counselling & admission",
    form_sub_text_color: "#101112",
    // Timer (optional — from variant 2)
    enable_timer: false,
    closed_in_label: "Admission Closed in",
    closed_in_label_color: "#9F1C34",
    end_date: "",
    end_time: "",
    timer_banner_background_color: "#F2E0E3",
    date_number_color: "#222222",
    date_text_color: "#222222",
    date_background_color: "#FFFFFF",
    // Step 1 button + consent
    button_step_1_text: "Sign Up",
    button_step_1_text_color: "#FFFFFF",
    button_background_color: "#9F1C34",
    checkbox_description:
      "By clicking on submit I allow DYP University to send program communication on email/sms/WhatsApp etc.",
    terms_conditions_link: "",
    privacy_policy_link: "",
    checkbox_description_color: "#101112",
    checkbox_color: "#9F1C34",
    // OTP step
    otp_description_text: "We’ve sent a 4-digit OTP to",
    otp_description_text_color: "#888888",
    edit_number_text: "Change Number",
    edit_number_text_color: "#9F1C34",
    otp_header_text: "Enter 4-digit OTP Here",
    otp_header_text_color: "#222222",
    resend_otp_text: "Resend OTP",
    resend_otp_text_color: "#9F1C34",
    button_step_2_text: "Verify & Continue",
    button_step_2_text_color: "#FFFFFF",
    button_step_2_background_color: "#9F1C34",
    // Last cards — shared circular SVG background + close icon
    success_circle_color: "#B75265",
    close_icon: "",
    close_icon_alt: "Close",
    // Last card — New user (Thank You)
    new_user_title: "Thank You",
    new_user_title_color: "#101112",
    new_user_subtitle: "We’ve Received Your Details!",
    new_user_subtitle_color: "#101112",
    new_user_description:
      "Our team will reach out soon to help you move forward with your application.",
    new_user_description_color: "#101112",
    new_user_button_text: "Back to Website",
    success_button_link: "",
    new_user_button_text_color: "#9F1C34",
    new_user_button_icon: "",
    new_user_background_image: "",
    new_user_background_image_alt: "Thank you",
    // Last card — Existing user (Welcome Back)
    existing_user_title: "Welcome Back!",
    existing_user_title_color: "#101112",
    existing_user_subtitle: "You’re Already Registered",
    existing_user_subtitle_color: "#101112",
    existing_user_description:
      "Our admissions team will be in touch shortly. Until then, explore the program to learn what awaits you.",
    existing_user_description_color: "#101112",
    existing_user_button_text: "Back to Website",
    existing_user_button_text_color: "#9F1C34",
    existing_user_button_icon: "",
    existing_user_background_image: "",
    existing_user_background_image_alt: "Welcome back",
    // Last card — Download (Downloaded Successfully)
    download_title: "Downloaded Successfully",
    download_title_color: "#101112",
    download_subtitle: "Review it carefully",
    download_subtitle_color: "#101112",
    download_description:
      "Your download will begin automatically. You can {{click here}} to download it manually..",
    download_description_color: "#101112",
    brochure_link_color: "#9F1C34",
    download_background_image: "",
    download_background_image_alt: "Downloaded successfully",
    // Popup display
    popup_display_interval_1: "",
    popup_display_interval_2: "",
    popup_display_interval_3: "",
    enable_delay_form: false,
    enable_exit_form: false,
  },

  LEAD_DROP_POPUP_FIELDS_CONFIG: [
    // ── Header ──
    {
      fieldName: "form_title",
      label: "Form Title",
      value: "Apply Now",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Enter Form Title",
      required: false,
      defaultValue: "Apply Now",
    },
    {
      fieldName: "form_title_color",
      label: "Form Title Color",
      value: "#101112",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "Enter Form Title Color (e.g., #101112)",
      required: false,
      defaultValue: "#101112",
    },
    {
      fieldName: "form_sub_text",
      label: "Form Sub Text",
      value: "For free career counselling & admission",
      valueType: "string",
      filedType: "TEXT_AREA",
      placeholder: "Enter Form Sub Text",
      required: false,
      defaultValue: "For free career counselling & admission",
      rows: 2,
    },
    {
      fieldName: "form_sub_text_color",
      label: "Form Sub Text Color",
      value: "#101112",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "Enter Form Sub Text Color (e.g., #101112)",
      required: false,
      defaultValue: "#101112",
    },
    // ── Timer (optional) ──
    {
      fieldName: "enable_timer",
      label: "Enable Countdown Timer",
      value: false,
      valueType: "boolean",
      filedType: "CHECKBOX",
      placeholder: "Show the 'Admission Closed in' countdown timer",
      required: false,
      isJsonField: true,
      defaultValue: false,
    },
    {
      fieldName: "closed_in_label",
      label: "Timer Label",
      value: "Admission Closed in",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Enter Timer Label (e.g., Admission Closed in)",
      required: false,
      isJsonField: true,
      defaultValue: "Admission Closed in",
    },
    {
      fieldName: "closed_in_label_color",
      label: "Timer Label Color",
      value: "#9F1C34",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "Enter Timer Label Color (e.g., #9F1C34)",
      required: false,
      isJsonField: true,
      defaultValue: "#9F1C34",
    },
    {
      fieldName: "end_date",
      label: "Timer End Date",
      value: "",
      valueType: "string",
      filedType: "DATE",
      placeholder: "Select end date",
      required: false,
      isJsonField: true,
      defaultValue: "",
    },
    {
      fieldName: "end_time",
      label: "Timer End Time",
      value: "",
      valueType: "string",
      filedType: "TIME",
      placeholder: "Select end time",
      required: false,
      isJsonField: true,
      defaultValue: "",
    },
    {
      fieldName: "timer_banner_background_color",
      label: "Timer Banner Background Color",
      value: "#F2E0E3",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "Enter Timer Banner Background Color (e.g., #F2E0E3)",
      required: false,
      isJsonField: true,
      defaultValue: "#F2E0E3",
    },
    {
      fieldName: "date_number_color",
      label: "Timer Number Color",
      value: "#222222",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "Enter Timer Number Color (e.g., #222222)",
      required: false,
      isJsonField: true,
      defaultValue: "#222222",
    },
    {
      fieldName: "date_text_color",
      label: "Timer Unit Text Color",
      value: "#222222",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "Enter Timer Unit Text Color (e.g., #222222)",
      required: false,
      isJsonField: true,
      defaultValue: "#222222",
    },
    {
      fieldName: "date_background_color",
      label: "Timer Box Background Color",
      value: "#FFFFFF",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "Enter Timer Box Background Color (e.g., #FFFFFF)",
      required: false,
      isJsonField: true,
      defaultValue: "#FFFFFF",
    },
    // ── Step 1 button + consent ──
    {
      fieldName: "button_step_1_text",
      label: "Submit Button Text",
      value: "Sign Up",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Enter Submit Button Text",
      required: false,
      isJsonField: true,
      defaultValue: "Sign Up",
    },
    {
      fieldName: "button_step_1_text_color",
      label: "Submit Button Text Color",
      value: "#FFFFFF",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "Enter Submit Button Text Color (e.g., #FFFFFF)",
      required: false,
      isJsonField: true,
      defaultValue: "#FFFFFF",
    },
    {
      // `lead_drop_popup` has a `button_color` column, NOT `button_background_color`
      // (that name is a `lead_drop_form` column). Saved top-level, Sequelize
      // silently dropped it and the picked colour never persisted — so this MUST
      // stay `isJsonField` and ride in `additional_fields`.
      fieldName: "button_background_color",
      label: "Button Background Color",
      value: "#9F1C34",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "Enter Button Background Color (e.g., #9F1C34)",
      required: false,
      isJsonField: true,
      defaultValue: "#9F1C34",
    },
    {
      fieldName: "checkbox_description",
      label: "Consent Text",
      value:
        "By clicking on submit I allow DYP University to send program communication on email/sms/WhatsApp etc.",
      valueType: "string",
      filedType: "TEXT_AREA",
      placeholder: "Enter Consent Text",
      required: false,
      defaultValue:
        "By clicking on submit I allow DYP University to send program communication on email/sms/WhatsApp etc.",
      rows: 2,
    },
    {
      fieldName: "terms_conditions_link",
      label: "Terms & Conditions Link",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "https://example.com/terms-and-conditions",
      required: false,
      defaultValue: "",
      description:
        "Destination URL for link slot 1. Referenced in Checkbox Description as {{1:Your link text}}.",
    },
    {
      fieldName: "privacy_policy_link",
      label: "Privacy Policy Link",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "https://example.com/privacy-policy",
      required: false,
      defaultValue: "",
      description:
        "Destination URL for link slot 2. Referenced in Checkbox Description as {{2:Your link text}}.",
    },
    {
      fieldName: "checkbox_description_color",
      label: "Consent Text Color",
      value: "#101112",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "Enter Consent Text Color (e.g., #101112)",
      required: false,
      defaultValue: "#101112",
    },
    {
      fieldName: "checkbox_color",
      label: "Checkbox Color",
      value: "#9F1C34",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "Enter Checkbox Color (e.g., #9F1C34)",
      required: false,
      defaultValue: "#9F1C34",
    },
    // ── OTP step ──
    {
      fieldName: "otp_description_text",
      label: "OTP Sent Text (number is appended automatically)",
      value: "We’ve sent a 4-digit OTP to",
      valueType: "string",
      filedType: "TEXT_AREA",
      placeholder: "Enter OTP Sent Text",
      required: false,
      isJsonField: true,
      defaultValue: "We’ve sent a 4-digit OTP to",
      rows: 2,
    },
    {
      fieldName: "otp_description_text_color",
      label: "OTP Sent Text Color",
      value: "#888888",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "Enter OTP Sent Text Color (e.g., #888888)",
      required: false,
      isJsonField: true,
      defaultValue: "#888888",
    },
    {
      fieldName: "edit_number_text",
      label: "Change Number Text",
      value: "Change Number",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Enter Change Number Text",
      required: false,
      isJsonField: true,
      defaultValue: "Change Number",
    },
    {
      fieldName: "edit_number_text_color",
      label: "Change Number Text Color",
      value: "#9F1C34",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "Enter Change Number Text Color (e.g., #9F1C34)",
      required: false,
      isJsonField: true,
      defaultValue: "#9F1C34",
    },
    {
      fieldName: "otp_header_text",
      label: "OTP Input Heading",
      value: "Enter 4-digit OTP Here",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Enter OTP Input Heading",
      required: false,
      isJsonField: true,
      defaultValue: "Enter 4-digit OTP Here",
    },
    {
      fieldName: "otp_header_text_color",
      label: "OTP Input Heading Color",
      value: "#222222",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "Enter OTP Input Heading Color (e.g., #222222)",
      required: false,
      isJsonField: true,
      defaultValue: "#222222",
    },
    {
      fieldName: "resend_otp_text",
      label: "Resend OTP Text",
      value: "Resend OTP",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Enter Resend OTP Text",
      required: false,
      isJsonField: true,
      defaultValue: "Resend OTP",
    },
    {
      fieldName: "resend_otp_text_color",
      label: "Resend OTP Text Color",
      value: "#9F1C34",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "Enter Resend OTP Text Color (e.g., #9F1C34)",
      required: false,
      isJsonField: true,
      defaultValue: "#9F1C34",
    },
    {
      fieldName: "button_step_2_text",
      label: "OTP Verify Button Text",
      value: "Verify & Continue",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Enter OTP Verify Button Text",
      required: false,
      isJsonField: true,
      defaultValue: "Verify & Continue",
    },
    {
      fieldName: "button_step_2_text_color",
      label: "OTP Verify Button Text Color",
      value: "#FFFFFF",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "Enter OTP Verify Button Text Color (e.g., #FFFFFF)",
      required: false,
      isJsonField: true,
      defaultValue: "#FFFFFF",
    },
    {
      fieldName: "button_step_2_background_color",
      label: "OTP Verify Button Background Color",
      value: "#9F1C34",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "Enter OTP Verify Button Background Color (e.g., #9F1C34)",
      required: false,
      isJsonField: true,
      defaultValue: "#9F1C34",
    },
    // ── Last cards: shared circular background + close ──
    {
      fieldName: "success_circle_color",
      label: "Last Card — Circular Background Color",
      value: "#B75265",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "Enter Circular Background Color (e.g., #B75265)",
      required: false,
      isJsonField: true,
      defaultValue: "#B75265",
    },
    {
      fieldName: "close_icon",
      label: "Close Icon",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Upload Close Icon",
      required: false,
      isJsonField: true,
      defaultValue: "",
    },
    {
      fieldName: "close_icon_alt",
      label: "Close Icon Alt Text",
      value: "Close",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Enter alt text for close icon",
      required: false,
      isJsonField: true,
      defaultValue: "Close",
    },
    // ── Last card: New user (Thank You) ──
    {
      fieldName: "new_user_title",
      label: "New User — Title",
      value: "Thank You",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Enter New User Title",
      required: false,
      isJsonField: true,
      defaultValue: "Thank You",
    },
    {
      fieldName: "new_user_title_color",
      label: "New User — Title Color",
      value: "#101112",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "Enter New User Title Color (e.g., #101112)",
      required: false,
      isJsonField: true,
      defaultValue: "#101112",
    },
    {
      fieldName: "new_user_subtitle",
      label: "New User — Subtitle",
      value: "We’ve Received Your Details!",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Enter New User Subtitle",
      required: false,
      isJsonField: true,
      defaultValue: "We’ve Received Your Details!",
    },
    {
      fieldName: "new_user_subtitle_color",
      label: "New User — Subtitle Color",
      value: "#101112",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "Enter New User Subtitle Color (e.g., #101112)",
      required: false,
      isJsonField: true,
      defaultValue: "#101112",
    },
    {
      fieldName: "new_user_description",
      label: "New User — Description",
      value:
        "Our team will reach out soon to help you move forward with your application.",
      valueType: "string",
      filedType: "TEXT_AREA",
      placeholder: "Enter New User Description",
      required: false,
      isJsonField: true,
      defaultValue:
        "Our team will reach out soon to help you move forward with your application.",
      rows: 3,
    },
    {
      fieldName: "new_user_description_color",
      label: "New User — Description Color",
      value: "#101112",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "Enter New User Description Color (e.g., #101112)",
      required: false,
      isJsonField: true,
      defaultValue: "#101112",
    },
    {
      fieldName: "new_user_button_text",
      label: "New User — Button Text",
      value: "Back to Website",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Enter New User Button Text",
      required: false,
      isJsonField: true,
      defaultValue: "Back to Website",
    },
    {
      fieldName: "new_user_button_text_color",
      label: "New User — Button Text/Border Color",
      value: "#9F1C34",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "Enter New User Button Color (e.g., #9F1C34)",
      required: false,
      isJsonField: true,
      defaultValue: "#9F1C34",
    },
    {
      fieldName: "new_user_button_icon",
      label: "New User — Button Icon",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Upload New User Button Icon",
      required: false,
      isJsonField: true,
      defaultValue: "",
    },
    {
      fieldName: "new_user_background_image",
      label: "New User — Image",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Upload New User Image",
      required: false,
      isJsonField: true,
      defaultValue: "",
    },
    {
      fieldName: "new_user_background_image_alt",
      label: "New User — Image Alt Text",
      value: "Thank you",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Enter alt text for new user image",
      required: false,
      isJsonField: true,
      defaultValue: "Thank you",
    },
    // ── Last card: Existing user (Welcome Back) ──
    {
      fieldName: "existing_user_title",
      label: "Existing User — Title",
      value: "Welcome Back!",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Enter Existing User Title",
      required: false,
      isJsonField: true,
      defaultValue: "Welcome Back!",
    },
    {
      fieldName: "existing_user_title_color",
      label: "Existing User — Title Color",
      value: "#101112",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "Enter Existing User Title Color (e.g., #101112)",
      required: false,
      isJsonField: true,
      defaultValue: "#101112",
    },
    {
      fieldName: "existing_user_subtitle",
      label: "Existing User — Subtitle",
      value: "You’re Already Registered",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Enter Existing User Subtitle",
      required: false,
      isJsonField: true,
      defaultValue: "You’re Already Registered",
    },
    {
      fieldName: "existing_user_subtitle_color",
      label: "Existing User — Subtitle Color",
      value: "#101112",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "Enter Existing User Subtitle Color (e.g., #101112)",
      required: false,
      isJsonField: true,
      defaultValue: "#101112",
    },
    {
      fieldName: "existing_user_description",
      label: "Existing User — Description",
      value:
        "Our admissions team will be in touch shortly. Until then, explore the program to learn what awaits you.",
      valueType: "string",
      filedType: "TEXT_AREA",
      placeholder: "Enter Existing User Description",
      required: false,
      isJsonField: true,
      defaultValue:
        "Our admissions team will be in touch shortly. Until then, explore the program to learn what awaits you.",
      rows: 3,
    },
    {
      fieldName: "existing_user_description_color",
      label: "Existing User — Description Color",
      value: "#101112",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "Enter Existing User Description Color (e.g., #101112)",
      required: false,
      isJsonField: true,
      defaultValue: "#101112",
    },
    {
      fieldName: "existing_user_button_text",
      label: "Existing User — Button Text",
      value: "Back to Website",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Enter Existing User Button Text",
      required: false,
      isJsonField: true,
      defaultValue: "Back to Website",
    },
    {
      fieldName: "existing_user_button_text_color",
      label: "Existing User — Button Text/Border Color",
      value: "#9F1C34",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "Enter Existing User Button Color (e.g., #9F1C34)",
      required: false,
      isJsonField: true,
      defaultValue: "#9F1C34",
    },
    {
      fieldName: "existing_user_button_icon",
      label: "Existing User — Button Icon",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Upload Existing User Button Icon",
      required: false,
      isJsonField: true,
      defaultValue: "",
    },
    {
      fieldName: "existing_user_background_image",
      label: "Existing User — Image",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Upload Existing User Image",
      required: false,
      isJsonField: true,
      defaultValue: "",
    },
    {
      fieldName: "existing_user_background_image_alt",
      label: "Existing User — Image Alt Text",
      value: "Welcome back",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Enter alt text for existing user image",
      required: false,
      isJsonField: true,
      defaultValue: "Welcome back",
    },
    // ── Last card: Download (Downloaded Successfully) ──
    {
      fieldName: "download_title",
      label: "Download — Title",
      value: "Downloaded Successfully",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Enter Download Title",
      required: false,
      isJsonField: true,
      defaultValue: "Downloaded Successfully",
    },
    {
      fieldName: "download_title_color",
      label: "Download — Title Color",
      value: "#101112",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "Enter Download Title Color (e.g., #101112)",
      required: false,
      isJsonField: true,
      defaultValue: "#101112",
    },
    {
      fieldName: "download_subtitle",
      label: "Download — Subtitle",
      value: "Review it carefully",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Enter Download Subtitle",
      required: false,
      isJsonField: true,
      defaultValue: "Review it carefully",
    },
    {
      fieldName: "download_subtitle_color",
      label: "Download — Subtitle Color",
      value: "#101112",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "Enter Download Subtitle Color (e.g., #101112)",
      required: false,
      isJsonField: true,
      defaultValue: "#101112",
    },
    {
      fieldName: "download_description",
      label:
        "Download — Description (wrap any text in {{ }} to turn it into the manual download link — it uses the Link Color below)",
      value:
        "Your download will begin automatically. You can {{click here}} to download it manually..",
      valueType: "string",
      filedType: "TEXT_AREA",
      placeholder: "Enter Download Description (e.g. ... {{click here}} ...)",
      required: false,
      isJsonField: true,
      defaultValue:
        "Your download will begin automatically. You can {{click here}} to download it manually..",
      rows: 3,
    },
    {
      fieldName: "download_description_color",
      label: "Download — Description Color",
      value: "#101112",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "Enter Download Description Color (e.g., #101112)",
      required: false,
      isJsonField: true,
      defaultValue: "#101112",
    },
    {
      fieldName: "brochure_link_color",
      label: "Download — Link Color (applied to the {{ }} text in the description)",
      value: "#9F1C34",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "Enter Manual Link Color (e.g., #9F1C34)",
      required: false,
      defaultValue: "#9F1C34",
    },
    {
      fieldName: "download_background_image",
      label: "Download — Image",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Upload Download Image",
      required: false,
      isJsonField: true,
      defaultValue: "",
    },
    {
      fieldName: "download_background_image_alt",
      label: "Download — Image Alt Text",
      value: "Downloaded successfully",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Enter alt text for download image",
      required: false,
      isJsonField: true,
      defaultValue: "Downloaded successfully",
    },
    // ── Popup display ──
    {
      fieldName: "popup_display_interval_1",
      label: "Popup Display Interval 1",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Enter Popup Display Interval 1 (e.g., 3000 - for 3 seconds)",
      required: false,
      isJsonField: true,
      defaultValue: "",
      validation: (value: any, formData: Record<string, any>) => {
        if (formData?.enable_delay_form && (!value || value.toString().trim() === "")) {
          return "Popup Display Interval 1 is required when Enable Delay Form is checked";
        }
        return null;
      },
    },
    {
      fieldName: "popup_display_interval_2",
      label: "Popup Display Interval 2",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Enter Popup Display Interval 2 (e.g., 6000 - for 6 seconds)",
      required: false,
      isJsonField: true,
      defaultValue: "",
    },
    {
      fieldName: "popup_display_interval_3",
      label: "Popup Display Interval 3",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Enter Popup Display Interval 3 (e.g., 12000 - for 12 seconds)",
      required: false,
      isJsonField: true,
      defaultValue: "",
    },
    {
      fieldName: "enable_delay_form",
      label: "Enable Delay Form",
      value: false,
      valueType: "boolean",
      filedType: "CHECKBOX",
      placeholder: "Enable form to show after delay",
      required: false,
      isJsonField: true,
      defaultValue: false,
    },
    {
      fieldName: "enable_exit_form",
      label: "Enable Exit Form",
      value: false,
      valueType: "boolean",
      filedType: "CHECKBOX",
      placeholder: "Enable form to show on exit intent",
      required: false,
      isJsonField: true,
      defaultValue: false,
    },
    {
      fieldName: "success_button_link",
      label:
        "Success button link (optional) — opens in a new tab when set; leave empty to keep the current behaviour (back to website / close)",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "https://example.com/... (leave empty to keep current behaviour)",
      required: false,
      isJsonField: true,
      defaultValue: "",
    },
  ],

  METADATA_FIELDS: [],
};
